import { type FC, useState } from "react";

import { Dialog, Paragraph } from "@fronterahealth/frontera-ui-components";

import { type PackageJson, parsePackagesJson } from "@pages/QARules/qaRuleLabels";

interface ImportPackagesDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onImport: (packages: PackageJson[]) => Promise<void>;
}

/** Paste exported package JSON (from here or the QA package manager) to recreate packages. */
export const ImportPackagesDialog: FC<ImportPackagesDialogProps> = ({ open, setOpen, onImport }) => {
  const [json, setJson] = useState("");
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);

  const handleImport = async () => {
    setError("");
    setImporting(true);
    try {
      await onImport(parsePackagesJson(json));
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      setOpen={setOpen}
      title="Import packages"
      size="2xl"
      primaryButton={{
        text: importing ? "Importing..." : "Import",
        onClick: handleImport,
        disabled: importing || !json,
      }}
      secondaryButton={{ text: "Cancel", onClick: () => setOpen(false) }}
    >
      <Paragraph className="mb-3 text-text-secondary">
        Paste a single package JSON object or an array of packages exported with &quot;Export JSON&quot;.
      </Paragraph>
      <textarea
        aria-label="Package JSON"
        className="w-full rounded border border-border-medium bg-bg-default px-3 py-2 font-mono text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-globemallow-600"
        placeholder='{"name": "...", "rules": [...], ...}'
        rows={12}
        value={json}
        onChange={(e) => setJson(e.target.value)}
      />
      {error && <p className="mt-1 text-xs text-text-error-primary">{error}</p>}
    </Dialog>
  );
};
